import { isDefined } from "@clipboard-health/util-ts";
import {
  useGetQuery,
  type UseGetQueryOptions,
  useInfiniteGetQuery,
  type UseInfiniteGetQueryOptions,
} from "@src/appV2/api";
import {
  type ChartingNoncomplianceState,
  type GetChartingCompletionReportsResponse,
  GetChartingCompletionReportsResponseSchema,
} from "@src/appV2/Charting/api/schema";
import { PAGE_SIZE } from "@src/appV2/Charting/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseInfiniteQueryResult } from "@tanstack/react-query";
import qs from "qs";

interface GetChartingCompletionReportsParams {
  workplaceId: string;
  qualifications: string[];
  startDate: string;
  endDate: string;
  workerNameSearch?: string;
  page?: {
    size?: number;
    cursor?: string;
  };
  chartingNoncomplianceState?: ChartingNoncomplianceState[];
}

export function useGetChartingCompletionReports(
  params: GetChartingCompletionReportsParams,
  options: UseGetQueryOptions<GetChartingCompletionReportsResponse> = {}
) {
  const {
    workplaceId,
    qualifications,
    startDate,
    endDate,
    workerNameSearch,
    page,
    chartingNoncomplianceState,
  } = params;
  const filter: Record<string, string> = {
    qualifications: qualifications.join(","),
    startDate,
    endDate,
    ...(isDefined(workerNameSearch) && { workerNameSearch }),
    ...(isDefined(chartingNoncomplianceState) && {
      chartingNoncomplianceState: chartingNoncomplianceState.join(","),
    }),
  };

  const queryParams: Record<string, unknown> = { filter };
  if (page) {
    queryParams.page = page;
  }

  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/charting-completion-reports`,
    queryParams,
    responseSchema: GetChartingCompletionReportsResponseSchema,
    meta: { logErrorMessage: APP_V2_APP_EVENTS.GET_CHARTING_COMPLETION_REPORTS_FAILURE },
    ...options,
  });
}

export function useInfiniteGetChartingCompletionReports(
  params: Omit<GetChartingCompletionReportsParams, "page"> & { pageSize?: number },
  options: UseInfiniteGetQueryOptions<GetChartingCompletionReportsResponse> = {}
): UseInfiniteQueryResult<GetChartingCompletionReportsResponse> {
  const {
    workplaceId,
    qualifications,
    startDate,
    endDate,
    workerNameSearch,
    pageSize = PAGE_SIZE,
    chartingNoncomplianceState,
  } = params;
  const filter: Record<string, string> = {
    qualifications: qualifications.join(","),
    startDate,
    endDate,
    ...(isDefined(workerNameSearch) && { workerNameSearch }),
    ...(isDefined(chartingNoncomplianceState) && {
      chartingNoncomplianceState: chartingNoncomplianceState.join(","),
    }),
  };

  return useInfiniteGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/charting-completion-reports`,
    queryParams: { filter, page: { size: pageSize } },
    responseSchema: GetChartingCompletionReportsResponseSchema,
    meta: { logErrorMessage: APP_V2_APP_EVENTS.GET_CHARTING_COMPLETION_REPORTS_FAILURE },
    getNextPageParam: (lastPage) => {
      if (!lastPage.links?.next) {
        return undefined;
      }

      try {
        // Parse the URL to extract nested query parameters
        const url = new URL(lastPage.links.next, environmentConfig.REACT_APP_BASE_API_URL);
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line import/no-named-as-default-member
        return qs.parse(url.search, { ignoreQueryPrefix: true });
      } catch {
        return undefined;
      }
    },
    ...options,
  });
}
